import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ReactComponent as GeometryDiagram } from '../../assets/geometry-diagram.svg';
import { RIDER_HEIGHTS, GEOMETRY_ROWS, GEOMETRY_SOURCE, framesFor, cmToFeet } from '../../data/geometry';

const WHATSAPP = 'https://wa.me/18297163555?text=';

const formatValue = (value, unit) => {
    if (value === null || value === undefined) return '–';
    const text = value.toLocaleString('es-DO');
    return unit === '°' ? `${text}°` : text;
};

// "Guía de tallas" and "Geometría" for a bike, with the data of the Raymon 2026 catalog
const SizeGeometryModal = ({ view, onClose, geometry, modelName, availableSizes = [], selectedFrame }) => {
    const panelRef = useRef(null);
    const frames = framesFor(geometry, selectedFrame);
    const [frameIndex, setFrameIndex] = useState(0);
    const frame = frames[Math.min(frameIndex, frames.length - 1)];

    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        panelRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [onClose]);

    const isSizeGuide = view === 'size';
    const title = isSizeGuide ? 'Guía de tallas' : 'Geometría';
    const helpText = encodeURIComponent(`Hola, quiero ayuda para elegir la talla de la ${modelName}. Mido `);

    // Rendered into <body> so no transformed parent can move or clip it
    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-6" onClick={onClose}>
            <div
                ref={panelRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-label={`${title} ${modelName}`}
                className="bg-white w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-xl outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-gray-100">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{title}</p>
                        <h2 className="text-2xl font-black italic uppercase tracking-tight text-black">{modelName}</h2>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Cerrar" className="p-2 -mr-2 rounded-full hover:bg-gray-100">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-6">
                    {isSizeGuide ? (
                        <>
                            <p className="text-gray-600 mb-5">Elige la talla según tu estatura.</p>
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="text-xs uppercase tracking-widest text-gray-400">
                                        <th className="py-2 pr-4 font-bold">Talla</th>
                                        <th className="py-2 pr-4 font-bold">Estatura</th>
                                        <th className="py-2 font-bold text-right">En cm</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(RIDER_HEIGHTS).map(([size, [from, to]]) => {
                                        const offered = availableSizes.length === 0 || availableSizes.includes(size);
                                        return (
                                            <tr key={size} className={`border-t border-gray-100 ${offered ? 'text-black' : 'text-gray-300'}`}>
                                                <td className="py-3 pr-4 font-black text-lg">{size}</td>
                                                <td className="py-3 pr-4 font-bold">{cmToFeet(from)} – {cmToFeet(to)}</td>
                                                <td className="py-3 text-right text-sm">{from} – {to} cm</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {availableSizes.length > 0 && (
                                <p className="text-xs text-gray-400 mt-3">En gris: tallas que no se ofrecen para este modelo.</p>
                            )}
                        </>
                    ) : (
                        <>
                            {frames.length > 1 && (
                                <div className="flex gap-2 mb-5" role="tablist" aria-label="Tipo de cuadro">
                                    {frames.map((f, i) => (
                                        <button
                                            key={f.frame}
                                            type="button"
                                            role="tab"
                                            aria-selected={i === frameIndex}
                                            onClick={() => setFrameIndex(i)}
                                            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest border ${i === frameIndex ? 'bg-black text-white border-black' : 'border-gray-300 text-gray-600 hover:border-black'}`}
                                        >
                                            {f.frame}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <GeometryDiagram className="w-full max-w-xl mx-auto h-auto text-gray-800 mb-6" aria-hidden="true" />
                            <div className="overflow-x-auto -mx-6 px-6">
                                <table className="w-full text-xs sm:text-sm border-collapse">
                                    <thead>
                                        <tr className="text-xs uppercase tracking-widest text-gray-400">
                                            <th className="py-2 pr-2 text-left font-bold">Medida <span className="sm:hidden normal-case tracking-normal">(mm)</span></th>
                                            {frame.sizes.map(size => (
                                                <th key={size} className="py-2 px-1 sm:px-2 text-right font-bold text-black">{size}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {GEOMETRY_ROWS.filter(row => frame.rows[row.key]).map(row => (
                                            <tr key={row.key} className="border-t border-gray-100">
                                                <td className="py-2 pr-2 text-gray-600 leading-snug">
                                                    {row.letter && <span className="inline-block w-5 font-bold text-black">{row.letter}</span>}
                                                    {row.label} {row.unit === 'mm' && <span className="hidden sm:inline text-gray-400">(mm)</span>}
                                                </td>
                                                {frame.rows[row.key].map((value, i) => (
                                                    <td key={i} className="py-2 px-1 sm:px-2 text-right font-medium text-black whitespace-nowrap">{formatValue(value, row.unit)}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <div className="mt-6 pt-5 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="text-xs text-gray-400">Fuente: {GEOMETRY_SOURCE}.</p>
                        <a
                            href={`${WHATSAPP}${helpText}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-bold uppercase tracking-widest text-black underline underline-offset-4"
                        >
                            ¿Dudas con la talla? Escríbenos
                        </a>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SizeGeometryModal;
